import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import {
  CreateClaimPayloadDto,
  FilterClaimsQueryDtoStatusesEnum,
} from 'libs/pinnacle-sdk';
import { getLocalDateForMidnightUTC, isUuid } from 'utils';
import * as yup from 'yup';

export interface ContractDateRange {
  inceptionDate: Date;
  expiryDate: Date;
}

export const getValidationSchema = (
  t: TFunction,
  contractDateRanges?: ContractDateRange[]
) =>
  yup.object().shape({
    insuredId: yup
      .string()
      .required(t('validation.required', { field: t('claim.insured') }))
      .test(
        'is-uuid',
        t('validation.invalid', { field: t('claim.insured') }),
        (value) => isUuid(value)
      ),

    status: yup
      .string()
      .oneOf(Object.values(FilterClaimsQueryDtoStatusesEnum))
      .required(t('validation.required', { field: t('claim.status') })),

    lineOfBusiness: yup
      .string()
      .required(t('validation.required', { field: t('claim.lineOfBusiness') })),

    subClass: yup
      .string()
      .required(t('validation.required', { field: t('claim.subClass') })),

    claimsHandler: yup
      .string()
      .required(t('validation.required', { field: t('claim.claimsHandler') })),

    dateReported: yup
      .date()
      .required(t('validation.required', { field: t('claim.dateReported') }))
      .test(
        'is-after-date-of-loss',
        t('claim.reportedDateMustBeAfterDateOfLoss'),
        function (value) {
          const dateOfLoss = this.parent.dateOfLoss;
          if (!value || !dateOfLoss) return true;

          const reportedDate = dayjs(value);
          const lossDate = dayjs(dateOfLoss);

          return reportedDate.isSameOrAfter(lossDate, 'day');
        }
      ),

    dateOfLoss: yup
      .date()
      .required(t('validation.required', { field: t('claim.dateOfLoss') }))
      .test(
        'is-within-contract-dates',
        t('claim.dateOfLossMustBeWithinContractDates'),
        function (value) {
          if (!value || !contractDateRanges || contractDateRanges.length === 0)
            return true;

          const lossDate = dayjs(value);

          const isWithinAnyContract = contractDateRanges.some((range) => {
            const inception = getLocalDateForMidnightUTC(range.inceptionDate);
            const expiry = getLocalDateForMidnightUTC(range.expiryDate);

            return (
              lossDate.isSameOrAfter(inception, 'day') &&
              lossDate.isSameOrBefore(expiry, 'day')
            );
          });

          return isWithinAnyContract;
        }
      ),

    catCode: yup
      .string()
      .required(t('validation.required', { field: t('claim.catCode') })),

    causeOfLoss: yup
      .string()
      .required(t('validation.required', { field: t('claim.causeOfLoss') })),

    currency: yup
      .string()
      .required(t('validation.required', { field: t('claim.currency') })),

    lossLocation: yup.string().optional(),

    city: yup
      .string()
      .required(t('validation.required', { field: t('general.city') })),

    country: yup
      .string()
      .required(t('validation.required', { field: t('general.country') })),

    lossDescription: yup.string().optional(),
  });

export const initialValues = {
  insuredId: undefined,
  status: undefined,
  lineOfBusiness: undefined,
  subClass: undefined,
  claimsHandler: undefined,
  dateReported: undefined,
  dateOfLoss: undefined,
  catCode: undefined,
  causeOfLoss: undefined,
  currency: undefined,
  lossLocation: undefined,
  city: undefined,
  country: undefined,
  lossDescription: undefined,
};

export const fields = Object.keys(
  initialValues
) as (keyof CreateClaimPayloadDto)[];

export const CLAIM_AUTO_NAVIGATE_DELAY = 3;
